<template>
  <v-app>
    <nav-bar
      v-if="this.$route.meta.navbar !== false"
      :logo-only="this.$route.meta.navbarLogoOnly"
    />
    <notification :navbar="this.$route.meta.navbar !== false" />
    <v-content>
      <router-view></router-view>
    </v-content>
    <Footer v-if="this.$route.meta.footer === true" />
  </v-app>
</template>

<script>
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import Notification from "@/components/Notification";
import { createNamespacedHelpers } from "vuex";

const { mapActions } = createNamespacedHelpers("constant");

import { redirectIfAuthExpired } from "@/services/security";

export default {
  name: "App",
  data: () => ({
    //
  }),
  components: {
    Footer,
    NavBar,
    Notification
  },
  created() {
    this.loadConstants();

    // not need to cancel interval as a redirect does that
    setInterval(() => redirectIfAuthExpired(this.$route), 10000);
  },
  methods: {
    ...mapActions(["loadConstants"])
  }
};
</script>

<style lang="sass">
.grey-text
  color: $grey-text-color

.svg-icon
  /* Vuetify recommends this: https://vuetifyjs.com/en/customization/icons/#component-icons */
  fill: currentColor

.theme--light.v-application
  background: $system-background !important

.dot
  border-radius: 50%
  width: 10px
  height: 10px
  background-color: #bbb
  display: inline-block

.v-divider
  border-color: $color-neutrals-navy-100 !important

.v-btn
  text-transform: none !important
  font-weight: 600
  letter-spacing: unset
  text-indent: unset

  &.tertiary
    background-color: $btn-bgcolor !important
    padding: 0 !important
    color: $primary
  &.underline-btn
    .v-btn__content
      border-bottom: 1px solid currentColor

  &.load-btn
    background-color: $btn-bgcolor !important
    color: $primary
    font-weight: 700
    text-transform: capitalize

.container
  .v-btn.v-btn--text
    font-weight: normal
    color: $subtitle

    &.inline
      color: $primary
      text-decoration: underline
      padding: 0
      vertical-align: unset
      height: unset

  h3 .v-btn.v-btn--text
    font-size: 24px

  &.full-height
    height: 100%
    display: flex
    flex-direction: column
    /* for tabs to work */
    .v-tabs
      flex-grow: 0
    .v-window
      flex-grow: 1
    .v-window, .v-window-item--active
      height: inherit
    /* for stepper to work */
    .v-stepper, .v-stepper__content, .v-stepper__wrapper
      height: 100%
    .v-stepper__items
      /*  72px is the height of the stepper header */
      height: calc(100% - 72px)

.v-card__actions
  .v-btn
    &.v-btn--text
      color: $text-btn-color

.back-nav
  height: 36px
  margin: 8px 0

.theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined).cta
  color: rgba(10,68,159,0.5) !important
  background-color: rgba(252,190,50,0.1) !important

.v-dialog.v-dialog--active
  box-shadow: none

.page-title
  font-size: 35px

h1,h2,h3,h4,h5,h6
  color: $color-pt-navy-500
  font-weight: 600
  h1
    font-size: 36px
    line-height: 48px
  h2
    font-size: 24px
    line-height: 32px
  h3
    font-size: 20px
    line-height: 24px
  h4
    font-size: 16px
    line-height: 24px

.h1-desktop-36
  font-size: 36px
  font-weight: 600
  line-height: 48px
  color: $color-neutrals-navy-400

.h3-desktop-regular-20
  font-size: 20px
  font-weight: 400
  line-height: 24px
  color: $color-neutrals-navy-400

.body-desktop-large-bold-18
  font-size: 18px
  font-weight: 600
  line-height: 24px
  color: $color-neutrals-navy-400

.body-desktop-medium-regular-16
  font-size: 16px
  font-weight: 400
  line-height: 24px
  color: $color-neutrals-navy-400

.body-desktop-medium-bold-16
  font-size: 16px
  font-weight: 600
  line-height: 24px
  color: $color-neutrals-navy-500

.body-desktop-small-regular-14
  font-size: 14px
  font-weight: 400
  line-height: 20px
  color: $color-neutrals-navy-400

.body-desktop-small-bold-14
  font-size: 14px
  font-weight: 600
  line-height: 20px
  color: $color-neutrals-navy-400

.title
  color: $color-neutrals-navy-400

img.center
    display: block
    margin: 0 auto

.main-table
  table
    overflow-x: hidden
  .v-data-table-header
    background-color: $table-header-bgcolor
    th
      font-weight: 600
      font-size: .875rem

  tbody
    tr.alert
      background-color: rgba(254,40,91,0.05)
    tr.warning
      background-color: rgba(252, 191, 50, 0.1) !important

    td
      font-size: 1rem
      border-bottom: 1px solid $table-border-color
      &:first-child
        border-left: 1px solid $table-border-color
      &:last-child
        border-right: 1px solid $table-border-color
    tr.v-data-table__empty-wrapper > td
      border-left: 1px solid $table-border-color
      border-right: 1px solid $table-border-color

.highlight
  font-weight: bold
  text-transform: uppercase

.description-text
  font-size: 14px
  line-height: 18px
  color: $grey-text-color
  margin-bottom: 4px

.secondary-nav.v-tabs
  .v-tabs-bar
    background-color: transparent !important
  .v-tab
    text-transform: none
    letter-spacing: unset
    text-indent: unset
    font-weight: 600
    font-size: 14px
    &.v-tab--active, &[aria-selected="true"]
      font-weight: 700
      text-decoration: underline
      color: $primary !important

// We need the following until we upgrade to 2.3+ which includes it
.text-decoration-underline
  text-decoration: underline

.v-form .v-input .v-label
  height: unset

.v-form .v-label
  line-height: 20px

.v-application .v-alert
  box-shadow: 1px 3px 12px 0 rgba(18, 40, 70, 0.1), 0 0 1px 0 rgba(18, 40, 70, 0.3)

.v-application .v-alert
  font-size: 14px
  font-weight: 400
  .v-icon
    font-size: 18px
  &.info
    color: $color-pt-blue-800 !important
    background-color: $color-pt-blue-100 !important
    .v-icon
      color: $color-pt-blue-500 !important
  &.success
    color: $color-semantic-state-green-800 !important
    background-color: $color-semantic-state-green-100 !important
    .v-icon
      color: $color-semantic-state-green-500 !important
  &.warning
    color: $color-semantic-state-yellow-800 !important
    background-color: $color-semantic-state-yellow-200 !important
    .v-icon
      color: $color-semantic-state-yellow-500 !important
  &.error
    color: $color-semantic-state-red-800 !important
    background-color: $color-semantic-state-red-100 !important
    .v-icon
      color: $color-semantic-state-red-500 !important

@media (min-width: 1264px)
  .v-application
    div.px-lg-16
      padding-right: 64px !important
      padding-left: 64px !important
@media (min-width: 600px)
    div.py-sm-14
      padding-top: 56px !important
      padding-bottom: 56px !important
.v-application
  .px-16
    padding-right: 64px !important
    padding-left: 64px !important
  a[disabled]
    cursor: default !important

@import "sass/salesforce-chat"
</style>
